/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Provider utils for widgets
 */

import _ from 'lodash';
import { providerFormFieldKeys } from 'components/forms/schema';
import { createIdentifier, createName } from 'utils/prepareFhirDataType';
import { getIdentifierValue, getName } from 'utils/parseFhirDataType';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { PROVIDER_STATUS, PROVIDER_TYPE } from 'app-constants/admin/provider';

const PROVIDER_IDENTIFIER_FIELD_NAME_SEPARATOR = '_';

const PROVIDER_IDENTIFIER_FIELDS_SUFFIX = `identifier${PROVIDER_IDENTIFIER_FIELD_NAME_SEPARATOR}`;
/**
 * attributes for add & edit Provider model
 */
const getProviderAttributes = (npi: string): Provider.IPostModel => {
	return {
		npi: npi || '',
		name: '',
		tin: '',
		activeStatus: PROVIDER_STATUS.active.value,
		resourceType: PROVIDER_TYPE.provider.providerType,
		humanName: [] as Fhir.HumanName[],
	};
};

/**
 * Add Provider Info form model
 */
const prepareProviderModel = (npi: string, provider?: Provider.IResponseModel): Provider.IProviderInitialModel => {
	const providerAttributes = getProviderAttributes(npi);
	const initialValues: Provider.IProviderInitialModel = _.mapValues(providerAttributes, (defaultValue, key) => {
		const value = _.get(provider, key);
		return _.isUndefined(value) ? defaultValue : value;
	});
	if (!provider) {
		return initialValues;
	} else if (provider?.providerPayerMeta) {
		_.mapValues(provider.providerPayerMeta, (identifierValues, payerId: string) => {
			_.mapValues(
				identifierValues,
				(identifierValue: string, identifierKey: keyof EligibilityRequestForm.IProviderIdentifierKeys) => {
					initialValues[
						`${PROVIDER_IDENTIFIER_FIELDS_SUFFIX}${payerId}${PROVIDER_IDENTIFIER_FIELD_NAME_SEPARATOR}${identifierKey}`
					] = identifierValue || '';
				},
			);
		});
	}
	return initialValues;
};

/**
 * provider form submit model for edit case
 */
const prepareProviderFormSubmitModel = (
	formFields: Provider.IPostModel & {
		[key: Provider.IProviderIdentifierFormKey]: string;
	},
	providerData: Provider.IResponseModel,
): Provider.IUpdateModel => {
	return _.entries(formFields).reduce((acc, [key, value]) => {
		if (_.startsWith(key, PROVIDER_IDENTIFIER_FIELDS_SUFFIX)) {
			const [, payer, identifier] = _.split(key, PROVIDER_IDENTIFIER_FIELD_NAME_SEPARATOR);
			// Initialize the payer group object if it doesn't exist
			if (!acc.providerPayerMeta) {
				acc.providerPayerMeta = {};
			}
			if (!acc?.providerPayerMeta[payer]) {
				acc.providerPayerMeta[payer] = {};
			}
			acc.providerPayerMeta[payer][identifier as keyof EligibilityRequestForm.IProviderIdentifierKeys] = value;
		} else {
			if (
				key === providerFormFieldKeys.humanName.key &&
				!!providerData &&
				formFields.resourceType === PROVIDER_TYPE.provider.providerType
			) {
				acc.humanName = providerData?.fhirResource?.name;
			} else {
				acc[key] = value;
			}
		}
		return acc;
	}, {} as Provider.IUpdateModel);
};

const trimValue = (value: string): string => _.trim(_.toLower(value));

// prepare provider fhir model structure
const prepareProviderFhirModel = (model: Provider.IUpdateModel, providerId?: string): Fhir.Practitioner | Fhir.Organization => {
	const identifierMapping = [
		{ value: model.npi, system: fhirSystemMapping.NPI },
		{ value: model?.tin, system: fhirSystemMapping.TaxId },
	];

	let humanName = model?.humanName || [];
	const firstName = humanName?.[0] || {};

	// family name should always be there
	if (
		model?.name &&
		model.resourceType === PROVIDER_TYPE.location.providerType &&
		_.isEmpty(firstName?.family) &&
		_.isEmpty(firstName?.given)
	) {
		const splitName = _.split(model?.name, ' ');
		const lastName = _.last(splitName);
		const givenName = _.dropRight(splitName);
		firstName.family = lastName || null;
		firstName.given = _.isEmpty(givenName) ? null : givenName;
		firstName.text = model?.name;
	}
	humanName = _.isEmpty(firstName) ? null : [{ ...firstName }];

	const identifier = _.map(
		_.filter(identifierMapping, (item) => !!item.value),
		(item) => createIdentifier(item),
	);
	let fhirResource: Fhir.Practitioner | Fhir.Organization =
		model.resourceType === PROVIDER_TYPE.provider.providerType
			? {
					name: humanName,
					resourceType: 'Practitioner',
				}
			: {
					name: model?.name,
					resourceType: 'Organization',
				};
	fhirResource = {
		...fhirResource,
		active: model.activeStatus,
	};
	if (!_.isEmpty(identifier)) {
		fhirResource.identifier = identifier;
	}
	if (providerId) {
		fhirResource.id = providerId;
	}
	return fhirResource;
};

// prepare provider post model structure
const prepareProviderPostModel = (model: Provider.IPostModel): Provider.IFhirPostModel => {
	return {
		providerType: model.resourceType,
		fhirResource: prepareProviderFhirModel(model),
	};
};

// prepare provider update model structure
const prepareProviderUpdateModel = (providerId: string, model: Provider.IUpdateModel): Provider.IFhirUpdateModel => {
	return {
		providerType: model.resourceType,
		fhirResource: prepareProviderFhirModel(model, providerId),
		...(!_.isEmpty(model.providerPayerMeta) ? { providerPayerMeta: model.providerPayerMeta } : {}),
	};
};

// function to parse the provider details
const getParsedProviderDetails = (fhirResource: Fhir.Practitioner | Fhir.Organization): Provider.IParsedProviderDetails => {
	return {
		id: fhirResource?.id,
		resourceType: fhirResource?.resourceType,
		name: getName(fhirResource),
		activeStatus: fhirResource?.active,
		npi: getIdentifierValue(fhirResource?.identifier, fhirSystemMapping.NPI),
		tin: getIdentifierValue(fhirResource?.identifier, fhirSystemMapping.TaxId),
		humanName: _.isArray(fhirResource?.name) ? fhirResource?.name : [],
	};
};

const getProviderPayerMeta = (
	payerMeta: Record<string, Provider.IProviderPayerMeta>,
): Record<string, Provider.IProviderPayerIdentifiersModel> => {
	const result = {};

	_.forEach(payerMeta, (value, key) => {
		const nonNullIdentifiers = _.pickBy(value?.identifiers, (identifier) => identifier !== null);

		if (!_.isEmpty(nonNullIdentifiers)) {
			result[key] = nonNullIdentifiers;
		}
	});

	return result;
};

const getRequestorResourceType = (registry: Provider.IRegistry): Provider.IResourceType => {
	return _.find(PROVIDER_TYPE, (type) => type.registryCode === registry?.enumeration_type)?.providerType;
};

const prepareHumanNameFromRegistry = (registry: Provider.IRegistry): Fhir.HumanName[] => {
	return [
		createName({
			givenName: _.compact([registry?.basic?.first_name, registry?.basic?.middle_name]),
			lastName: registry?.basic?.last_name,
		}),
	];
};

export {
	PROVIDER_IDENTIFIER_FIELDS_SUFFIX,
	PROVIDER_IDENTIFIER_FIELD_NAME_SEPARATOR,
	prepareProviderModel,
	getProviderAttributes,
	prepareProviderFormSubmitModel,
	trimValue,
	prepareProviderPostModel,
	prepareProviderUpdateModel,
	getParsedProviderDetails,
	getProviderPayerMeta,
	getRequestorResourceType,
	prepareHumanNameFromRegistry,
};
